/**
 * The Charter Reach subsystem (M5 spec §12, §14.1-§14.2, §18 flows 1-2; plan C14): the local layer of the law.
 * Discovered from `src/<dir>/subsystem.ts` (M3); name `charter`, order 460 (after traffic, 450).
 *
 * - Reads the strategic client through `strategicBoot()?.client` (like traffic) and mirrors the local system from
 *   its manifest and system view (stations, owners, zones, the claim holder) into a `LawWorld`.
 * - **Relations:** `applyRelations(view)` takes each `RelationsView` (from `AdvanceResult.relations`). The client
 *   forwards it through `onRelations` when it has one (a gate item: M4's client drops it today).
 * - **Hostility** (`hostilityWriter.ts`): rewritten on a new view, a holder change or the hold gaining or losing
 *   tetherstone.
 * - **Crimes** (`crimeDetector.ts`) on the sim's `deaths` stage → `reportCrime`; standing deltas in the result go to
 *   the bound `applyStandingDeltas` port (the Career).
 * - **Scans** (`scans.ts`): the patrol scan while tetherstone is illegal and in the hold; the docking scan through
 *   the policy. A finding reports `smuggling` against the jurisdiction, or the Authority where there is none
 *   (corporation-held, unclaimed or outlaw-held space; M5 spec §27.2, D-424), and confiscates through the
 *   `confiscateTetherstone` port. The corporation's own stations never scan (§27.3).
 * - **Docking** (`dockingPolicy.ts`): installed into M2's clearance on `init` (S-M2-11).
 * - **Fines** (`fines.ts`): `payCharterFines(career)` for the comms prompt and the Registry panel (C18 draws them).
 *
 * M2's pieces (space events, station ids, the hold) come through `bindCharterPorts`, bound by whoever holds M2
 * (boot, like `bindTrafficPorts`). Unbound, the matching parts stay idle. Per-tick work allocates nothing.
 */
import { SIM_DT } from '../core/loop'
import { logger } from '../core/logger'
import { installDockingPolicy } from '../docking/clearance'
import type { SystemLayout } from '../galaxy/layout/types'
import type { CareerPort } from '../reach/ports'
import { siteRadius } from '../reach/session/siteRadius'
import type { ReachSubsystem } from '../reach/runtime/types'
import { registerSimStage, type Sim } from '../sim'
import type { SpaceEventRing } from '../space/events'
import type { StrategicClient } from '../strategic/client'
import { strategicBoot } from '../strategic-link/service'
import { Agent } from '../traffic/components'
import { activeTrafficStore } from '../traffic/subsystem'
import { Pos64 } from '../world/components'
import {
  FACTION,
  FACTION_IDS,
  type CommandResult,
  type CrimeKind,
  type FactionId,
  type LocalManifest,
  type RelationsView,
  type StandingDelta,
  type StationView,
  type SystemEconomyView,
} from '../strategic/types'
import {
  clearLawWorld,
  createCrimeDetector,
  createLawWorld,
  fillLawWorld,
  resetCrimeDetector,
  stepCrimes,
  viewStationRadius,
  type CrimeDetector,
  type LawWorld,
} from './crimeDetector'
import { createCharterDockingPolicy, type CharterDockingPolicy } from './dockingPolicy'
import { clearMirroredBounties, defaultFinesTuning, finesStationInRange, payFines, type PayFinesResult } from './fines'
import { writeHostility, type HostilityInputs } from './hostilityWriter'
import { createCharterConflict } from './zones'
import { createPatrolScan, resetPatrolScan, stepPatrolScan, type PatrolScanState } from './scans'

/** M2's pieces the law reads (bound by boot or the M2 bridge; null = that part idles). */
export interface CharterPorts {
  /** M2's space event ring (wrecks and secured parts, for piracy). */
  space: { readonly events: SpaceEventRing } | null
  /** M2's station index → its station def id. */
  stationDefId: ((index: number) => string | null) | null
  /** R3-1 sweep: the live system layout (home-cluster site ids → M2's ids for the docking policy). */
  layout?: (() => Pick<SystemLayout, 'stations' | 'home'> | null) | null
  /** Tonnes of tetherstone in the player's hold. */
  tetherstoneT: (() => number) | null
  /** Removes `tonnes` of tetherstone from the hold (a smuggling finding). */
  confiscateTetherstone: ((tonnes: number) => void) | null
  /** Applies standing deltas from a command result to the Career (S-M2-13). */
  applyStandingDeltas: ((deltas: readonly StandingDelta[]) => void) | null
  /** M7 S37: a crime the core recorded (the story link's `crimeReported`). Law semantics are unchanged (D-424). */
  onCrime?: ((kind: CrimeKind, victimFaction: FactionId, systemId: string) => void) | null
  /** M7 S37: a contraband story item is aboard (the patrol scan runs for it too; called per tick, allocation-free). */
  storyContraband?: (() => boolean) | null
  /** M7 S37: a finished patrol scan with a contraband story item aboard, by the patrol's faction (T6, D-422). */
  onStoryScan?: ((patrolFaction: FactionId) => void) | null
}

const ports: CharterPorts = {
  space: null,
  stationDefId: null,
  layout: null,
  tetherstoneT: null,
  confiscateTetherstone: null,
  applyStandingDeltas: null,
  onCrime: null,
  storyContraband: null,
  onStoryScan: null,
}

/** Binds (or, with nulls, unbinds) the M2 ports every Charter runtime reads. */
export function bindCharterPorts(p: Partial<CharterPorts>): void {
  if (p.space !== undefined) ports.space = p.space
  if (p.stationDefId !== undefined) ports.stationDefId = p.stationDefId
  if (p.layout !== undefined) ports.layout = p.layout
  if (p.tetherstoneT !== undefined) ports.tetherstoneT = p.tetherstoneT
  if (p.confiscateTetherstone !== undefined) ports.confiscateTetherstone = p.confiscateTetherstone
  if (p.applyStandingDeltas !== undefined) ports.applyStandingDeltas = p.applyStandingDeltas
  if (p.onCrime !== undefined) ports.onCrime = p.onCrime
  if (p.storyContraband !== undefined) ports.storyContraband = p.storyContraband
  if (p.onStoryScan !== undefined) ports.onStoryScan = p.onStoryScan
}

/** The client calls the Charter makes. */
export type CharterClient = Pick<StrategicClient, 'command' | 'onManifest' | 'onSystemView'> &
  Partial<Pick<StrategicClient, 'manifest' | 'systemView'>>

/** What the client offers once it forwards `AdvanceResult.relations` (gate item). */
interface RelationsFeed {
  onRelations(fn: (view: RelationsView) => void): () => void
}

function hasRelationsFeed(c: object): c is RelationsFeed {
  return typeof (c as Partial<RelationsFeed>).onRelations === 'function'
}

export interface CharterRuntime {
  readonly world: LawWorld
  readonly detector: CrimeDetector
  readonly scan: PatrolScanState
  readonly policy: CharterDockingPolicy
  readonly hostility: HostilityInputs
  /** The latest relations (null before the first). */
  relations: RelationsView | null
  /** Tetherstone is banned (vote result; set by `setTetherstoneIllegal`). */
  tetherstoneIllegal: boolean
  client: CharterClient | null
  /** The hostility inputs changed: rewrite the matrix on the next step. */
  hostilityDirty: boolean
  lastHostilityKey: number
  stagedSim: Sim | null
  unstage: (() => void) | null
  /** Bound once (a per-tick closure would allocate). */
  readonly report: (kind: CrimeKind, victim: number, value: number) => void
  readonly isPatrol: (eid: number) => boolean
}

function logSendError(err: unknown): void {
  logger.warn('charter: a report to the strategic core failed', err)
}

function tetherstoneT(): number {
  return ports.tetherstoneT?.() ?? 0
}

function isTrafficPatrol(eid: number): boolean {
  const s = activeTrafficStore()
  if (s === null || eid < 0) return false
  const slot = Agent.index[eid]
  return slot >= 0 && slot < s.count && s.eid[slot] === eid && s.role[slot] === 'patrol'
}

/**
 * Sends `reportCrime` (message rate); logs it for the dev console and hands standing deltas to the Career. Returns
 * whether it was sent (false with no client, no local system or no victim faction).
 */
export function sendCrime(rt: CharterRuntime, kind: CrimeKind, victim: number, value: number): boolean {
  const client = rt.client
  const systemId = rt.world.systemId
  if (client === null || systemId === null || victim < 0) return false
  const victimFaction = FACTION_IDS[victim]
  client
    .command({ type: 'reportCrime', kind, victimFaction, systemId, value })
    .then((r: CommandResult) => {
      if (!r.ok) {
        logger.debug(`charter: ${kind} against ${victimFaction} not recorded (${r.error})`)
        return
      }
      logger.info(`charter: ${kind} against ${victimFaction} reported in ${systemId}`)
      ports.onCrime?.(kind, victimFaction, systemId)
      if (r.standingDeltas !== undefined && r.standingDeltas.length > 0) ports.applyStandingDeltas?.(r.standingDeltas)
    })
    .catch(logSendError)
  return true
}

/** The Charter's own enforcer: law.json `fines.faction` (the Authority), which books a ban with no jurisdiction. */
const SMUGGLING_AUTHORITY: number = FACTION[defaultFinesTuning().faction]

/**
 * The faction a smuggling finding is reported against (M5 spec §27.2, D-424): the jurisdiction when there is one,
 * else the Authority (a Charter ban in corporation-held, unclaimed or outlaw-held space). Never the corporation.
 */
export function smugglingVictim(w: LawWorld): number {
  return w.jurisdiction >= 0 ? w.jurisdiction : SMUGGLING_AUTHORITY
}

/** A smuggling finding: report it (`smugglingVictim`) and, only once a crime is sent, confiscate (B2). */
function smuggling(rt: CharterRuntime, tonnes: number): void {
  if (!sendCrime(rt, 'smuggling', smugglingVictim(rt.world), tonnes)) return
  ports.confiscateTetherstone?.(tonnes)
  rt.hostilityDirty = true
}

export function createCharterRuntime(): CharterRuntime {
  const world = createLawWorld()
  const rt: CharterRuntime = {
    world,
    detector: createCrimeDetector(),
    scan: createPatrolScan(),
    policy: createCharterDockingPolicy({
      world,
      relations: () => rt.relations,
      stationDefId: (i) => ports.stationDefId?.(i) ?? null,
      layout: () => ports.layout?.() ?? null,
      tetherstoneIllegal: () => rt.tetherstoneIllegal,
      tetherstoneT,
      onSmuggling: (t) => smuggling(rt, t),
    }),
    hostility: { view: null, holder: -1, tetherstone: false },
    relations: null,
    tetherstoneIllegal: false,
    client: null,
    hostilityDirty: true,
    lastHostilityKey: -1,
    stagedSim: null,
    unstage: null,
    report: (kind, victim, value) => sendCrime(rt, kind, victim, value),
    isPatrol: isTrafficPatrol,
  }
  return rt
}

/** Takes a new `RelationsView` (from `AdvanceResult.relations`): the hostility matrix is rewritten next step. */
export function applyRelations(rt: CharterRuntime, view: RelationsView): void {
  rt.relations = view
  rt.hostilityDirty = true
}

/** Sets whether tetherstone is illegal (the Charter ban; spec §11). */
export function setTetherstoneIllegal(rt: CharterRuntime, illegal: boolean): void {
  rt.tetherstoneIllegal = illegal
}

/**
 * A station's gameplay radius for the law world (scale SC-5e2; message rate): `siteRadius` of its layout site when the
 * live layout (`ports.layout`) has it, so a home station gets M2's model the runtime shows (D-912), else the view's
 * model (`stationRadiusOf(assetId)`, e.g. a station built since the layout was made).
 */
export function lawStationRadius(s: StationView): number {
  const layout = ports.layout?.() ?? null
  if (layout !== null) {
    const sites = layout.stations
    for (let i = 0; i < sites.length; i++) if (sites[i].id === s.id) return siteRadius(sites[i])
  }
  return viewStationRadius(s)
}

/** Mirrors a manifest (a system was entered). */
export function applyManifest(rt: CharterRuntime, m: LocalManifest): void {
  const holder = m.stations.length > 0 ? m.stations[0].holder : null
  fillLawWorld(rt.world, m.systemId, m.stations, m.zones, holder, lawStationRadius)
  rt.hostilityDirty = true
}

/** Mirrors a system view: the tetherstone ban (global; any system's view carries it), then the local system's claims, zones and stations. */
export function applySystemView(rt: CharterRuntime, v: SystemEconomyView): void {
  setTetherstoneIllegal(rt, v.tetherstoneIllegal)
  if (v.systemId !== rt.world.systemId) return
  fillLawWorld(rt.world, v.systemId, v.stations, v.zones, v.claim.holder, lawStationRadius)
  rt.hostilityDirty = true
}

/**
 * Binds the client (once): manifests, system views and, when offered, relations. The runtime binds on its first step,
 * after the start system's manifest was emitted, so the client's current manifest and system view are applied at
 * once (D-TBD, C21: otherwise the start system had no law world and no crime there was ever reported).
 */
export function bindCharterClient(rt: CharterRuntime, client: CharterClient): void {
  rt.client = client
  client.onManifest((m) => applyManifest(rt, m))
  client.onSystemView((v) => applySystemView(rt, v))
  if (hasRelationsFeed(client)) client.onRelations((v) => applyRelations(rt, v))
  const m = client.manifest ?? null
  if (m !== null) applyManifest(rt, m)
  const v = client.systemView ?? null
  if (v !== null) applySystemView(rt, v)
}

/** Registers the crime detector on `sim`'s `deaths` stage (once per sim), skipping the events before it. */
export function stageCharter(rt: CharterRuntime, sim: Sim): void {
  if (rt.stagedSim === sim) return
  rt.unstage?.()
  rt.stagedSim = sim
  const d = rt.detector
  resetCrimeDetector(d)
  d.cursor = sim.events.head
  d.spaceCursor = ports.space?.events.head ?? 0
  rt.unstage = registerSimStage(sim, 'deaths', (s) => stepCrimes(d, s, rt.world, ports.space?.events ?? null, rt.report))
}

/** One Charter step (per sim tick). Allocation-free on a quiet tick. */
export function stepCharter(rt: CharterRuntime, sim: Sim, dt: number = SIM_DT): void {
  stageCharter(rt, sim)
  const t = tetherstoneT()
  const h = rt.hostility
  const key = (rt.world.holder + 1) * 2 + (t > 0 ? 1 : 0)
  if (rt.hostilityDirty || key !== rt.lastHostilityKey) {
    rt.hostilityDirty = false
    rt.lastHostilityKey = key
    h.view = rt.relations
    h.holder = rt.world.holder
    h.tetherstone = t > 0
    writeHostility(h)
  }
  // M7 S37: a contraband story item draws the patrol scan too; it is fined through the story (T6), never confiscated.
  const story = ports.storyContraband?.() ?? false
  const j = rt.world.jurisdiction
  if (stepPatrolScan(rt.scan, sim, dt, j, rt.tetherstoneIllegal, t, rt.isPatrol, story)) {
    if (rt.tetherstoneIllegal && t > 0) smuggling(rt, t)
    if (story && j >= 0) ports.onStoryScan?.(FACTION_IDS[j])
  }
}

/** The system was left: the local mirror, the scan and the per-ship memory start over. */
export function leaveCharter(rt: CharterRuntime): void {
  clearLawWorld(rt.world)
  resetPatrolScan(rt.scan)
  resetCrimeDetector(rt.detector)
  rt.hostilityDirty = true
}

/** True when a station of the fines faction has the player within range of its hull (the comms "Pay fines" prompt; SC-5e2). */
export function canPayFinesHere(rt: CharterRuntime, sim: Sim): boolean {
  const p = sim.playerEid
  if (p < 0) return false
  return finesStationInRange(rt.world, Pos64.x[p], Pos64.y[p], Pos64.z[p])
}

/** Pays every fine through the Career (flow 2). On success the mirrored bounties clear and hostility is rewritten. */
export async function payCharterFines(rt: CharterRuntime, career: Pick<CareerPort, 'spend' | 'earn'>): Promise<PayFinesResult> {
  const client = rt.client
  if (client === null) return { status: 'failed', error: 'no strategic client' }
  const r = await payFines({ career, client, view: rt.relations })
  if (r.status === 'paid') {
    clearMirroredBounties(rt.relations)
    rt.hostilityDirty = true
    logger.info(`charter: fines paid (${r.amount})`)
  }
  return r
}

let active: CharterRuntime | null = null

/** The live Charter runtime (C18's HUD and panels, C20's debug API), or null before the Reach runs. */
export function activeCharter(): CharterRuntime | null {
  return active
}

export default function createCharterSubsystem(): ReachSubsystem {
  const rt = createCharterRuntime()
  active = rt
  // C15's local conflict (zones, war bands, blockades, the live toll policy), stepped with the Charter.
  const conflict = createCharterConflict({ client: () => strategicBoot()?.client ?? null, store: activeTrafficStore, dt: SIM_DT })
  return {
    name: 'charter',
    order: 460,
    init(): void {
      installDockingPolicy(rt.policy)
    },
    step(_core, sim): void {
      if (rt.client === null) {
        const client = strategicBoot()?.client
        if (client === undefined) return
        bindCharterClient(rt, client)
      }
      stepCharter(rt, sim)
      conflict.step(sim)
    },
    onSystemLeave(): void {
      leaveCharter(rt)
      conflict.leave()
    },
  }
}
